import Button from "components/Button/Button.tsx";
import { KaboodleToaster } from "components/common";
import React, { useState, useRef, useEffect } from "react";
import type { SavedView } from "services/saved-view-api";

import { useReportActions } from "../contexts/ReportActionsContext";
import FinanceFilterPresetButtons from "../filters/components/FilterPresetButtons/FinanceFilterPresetButtons";

type FinanceHeaderActionsProps = {
  currentUrl: string;
  onLoadPreset: (preset: SavedView) => void;
  currentPreset?: SavedView | null;
};

// renders the trio of action buttons in the header: Export | Save | Load
// combines export functionality with preset save/load functionality
const FinanceHeaderActions: React.FC<FinanceHeaderActionsProps> = ({
  currentUrl,
  onLoadPreset,
  currentPreset = null,
}) => {
  const { state } = useReportActions();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showExportMenu]);

  const {
    reportType,
    isGenerating,
    onGenerateReport,
    onGenerateLedesReport,
    onShareReport,
  } = state;

  const handleShareReport = () => {
    if (onShareReport) {
      onShareReport();
      KaboodleToaster.success("Report URL copied to clipboard");
    }
  };

  return (
    <div className="finance-header-actions-trio" ref={exportMenuRef}>
      {/* export dropdown button */}
      <Button
        onClick={() => setShowExportMenu(!showExportMenu)}
        disabled={isGenerating}
        className="header-action-trio-button export-dropdown-button"
      >
        <span className="material-icons">file_download</span>
        <span>Export</span>
        <span className="material-icons dropdown-icon">
          {showExportMenu ? "expand_less" : "expand_more"}
        </span>
      </Button>

      {/* save and load buttons using the preset component */}
      <FinanceFilterPresetButtons
        currentUrl={currentUrl}
        onLoadPreset={onLoadPreset}
        currentPreset={currentPreset}
      />

      {/* export dropdown menu */}
      {showExportMenu && (
        <div className="export-dropdown-menu">
          {onGenerateReport && (
            <button
              className="export-menu-item"
              onClick={() => {
                onGenerateReport();
                setShowExportMenu(false);
              }}
              disabled={isGenerating}
              type="button"
            >
              <span className="material-icons">download</span>
              <span>Download Report</span>
            </button>
          )}

          {reportType === "INVOICE_LIST" && onGenerateLedesReport && (
            <button
              className="export-menu-item"
              onClick={() => {
                onGenerateLedesReport();
                setShowExportMenu(false);
              }}
              disabled={isGenerating}
              type="button"
            >
              <span className="material-icons">description</span>
              <span>Export LEDES</span>
            </button>
          )}

          {onShareReport && (
            <button
              className="export-menu-item"
              onClick={() => {
                handleShareReport();
                setShowExportMenu(false);
              }}
              type="button"
            >
              <span className="material-icons">share</span>
              <span>Copy Share Link</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FinanceHeaderActions;
